import React from 'react';
import { HeroImage } from './types';
import { MobileImageCarousel } from './MobileImageCarousel';

interface VehicleHeroGalleryProps {
  images: HeroImage[];
  isOwner: boolean;
  onImageClick: (index: number) => void;
  onEditImage: (index: number) => void;
  onDeleteImage: (index: number) => void;
  onMoveImage: (fromIndex: number, toIndex: number) => void;
  onAddImage: () => void;
  /** Car-show / iPad kiosk layout — large swipeable gallery */
  showcaseMode?: boolean;
  /** When true, gallery fills a sticky side panel (landscape iPad) */
  showcasePanel?: boolean;
}

export const VehicleHeroGallery: React.FC<VehicleHeroGalleryProps> = ({
  images,
  isOwner,
  onImageClick,
  onEditImage,
  onDeleteImage,
  onMoveImage,
  onAddImage,
  showcaseMode = false,
  showcasePanel = false,
}) => {
  // Side-panel showcase stays swipeable; normal build pages: carousel on mobile only.
  const forceCarousel = showcasePanel;
  const carouselSize = showcasePanel ? 'showcase' : showcaseMode ? 'tablet' : 'default';
  const autoPlay = !isOwner;

  const shellClass = showcasePanel
    ? 'h-full bg-[#0c0c0c]/50 border-b lg:border-b-0 lg:border-r border-white/[0.06] flex flex-col'
    : 'bg-[#0c0c0c]/50 border-b border-white/[0.06]';

  const innerClass = showcasePanel
    ? 'flex-1 flex flex-col px-4 sm:px-6 py-4 lg:py-6 min-h-0'
    : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 md:py-8';

  return (
    <div className={shellClass}>
      <div className={innerClass}>
        {/* Carousel: phones only (and sticky showcase side panel) */}
        <div className={forceCarousel ? 'block' : 'block md:hidden'}>
          <MobileImageCarousel
            images={images}
            isOwner={isOwner}
            onImageClick={onImageClick}
            onEditImage={onEditImage}
            onDeleteImage={onDeleteImage}
            onMoveImage={onMoveImage}
            onAddImage={onAddImage}
            size={carouselSize}
            autoPlay={autoPlay}
          />
          {showcaseMode && !isOwner && images.length > 0 && (
            <p className="text-center text-xs sm:text-sm text-gray-500 mt-3 md:hidden">
              Swipe for more · Tap to enlarge
            </p>
          )}
        </div>

        {/* Photo grid: tablet and up */}
        <div className={forceCarousel ? 'hidden' : 'hidden md:grid grid-cols-3 lg:grid-cols-4 gap-3 lg:gap-4'}>
          {images.map((image, index) => (
            <div
              key={index}
              className="relative group cursor-pointer"
              onClick={(e) => {
                if ((e.target as HTMLElement).closest('button')) {
                  return;
                }
                onImageClick(index);
              }}
            >
              <div className="aspect-square bg-[#111] rounded-xl overflow-hidden border border-white/[0.07] hover:border-orange-500/30 transition-all duration-200">
                <img
                  src={image.src}
                  alt={image.alt}
                  loading={index === 0 ? 'eager' : 'lazy'}
                  className="w-full h-full object-cover transition-transform group-hover:scale-105"
                />
              </div>

              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-xl pointer-events-none">
                <div className="absolute bottom-3 left-3 right-3">
                  <h3 className="text-white text-sm font-medium truncate backdrop-blur-sm bg-black/30 px-2 py-1 rounded">
                    {image.title}
                  </h3>
                </div>
              </div>

              {isOwner && (
                <div
                  className="absolute top-3 right-3 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity z-20"
                  onClick={(e) => e.stopPropagation()}
                >
                  {index > 0 && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onMoveImage(index, index - 1);
                      }}
                      className="p-1.5 bg-orange-500/80 backdrop-blur-sm hover:bg-orange-500 text-white rounded-lg transition-colors border border-orange-400/30 z-30"
                      title="Move left"
                      type="button"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                  )}
                  {index < images.length - 1 && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onMoveImage(index, index + 1);
                      }}
                      className="p-1.5 bg-orange-500/80 backdrop-blur-sm hover:bg-orange-500 text-white rounded-lg transition-colors border border-orange-400/30 z-30"
                      title="Move right"
                      type="button"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onEditImage(index);
                    }}
                    className="p-1.5 bg-[#141414]/80 backdrop-blur-sm hover:bg-white/[0.06] text-white rounded-lg transition-colors border border-white/[0.08] z-30"
                    title="Edit image"
                    type="button"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onDeleteImage(index);
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    className="p-1.5 bg-red-500/80 backdrop-blur-sm hover:bg-red-500 text-white rounded-lg transition-colors border border-red-400/30 z-30 cursor-pointer"
                    title="Delete image"
                    type="button"
                    style={{ pointerEvents: 'auto' }}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          ))}

          {images.length > 0 && images.length < 4 && isOwner &&
            Array.from({ length: 4 - images.length }).map((_, index) => (
              <div
                key={`empty-${index}`}
                onClick={onAddImage}
                className="aspect-square bg-[#0c0c0c]/50 rounded-xl border-2 border-dashed border-white/[0.08] hover:border-orange-500/50 cursor-pointer transition-all duration-200 flex items-center justify-center group hover:bg-white/[0.04]/40"
              >
                <div className="text-center px-3">
                  <svg className="w-8 h-8 text-gray-500 group-hover:text-orange-400 mx-auto mb-2 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <p className="text-meta group-hover:text-orange-400 transition-colors">Add photo</p>
                </div>
              </div>
            ))}

          {images.length === 0 && !isOwner && (
            <div className="col-span-full text-center py-12 card">
              <svg className="w-16 h-16 text-gray-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-muted">No photos available</p>
            </div>
          )}

          {images.length === 0 && isOwner && (
            <div className="col-span-full text-center py-12 card">
              <p className="text-muted text-sm mb-4">No photos yet</p>
              <button type="button" onClick={onAddImage} className="btn-primary">
                Add photos
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
